import type { ReactNode } from 'react';
import { DateTime, Num, Plural, T, Var, useGT } from 'gt-react';
import {
  CaretRightIcon,
  DownloadSimpleIcon,
  FileXlsIcon,
  PlayIcon,
  WarningIcon,
  XIcon,
} from '@phosphor-icons/react';
import { BackgroundJobStatus } from '@erp/shared-types';
import type {
  FeedbackSupplierProfile,
  MassRenewalBuiltDivision,
  MassRenewalDivisionWarning,
  MassRenewalDownloadFile,
  MassRenewalRun,
  MassRenewalRunReport,
  MassRenewalSkippedDivision,
} from '@erp/rpc';

import {
  AlertContainer,
  AlertDescription,
  AlertTitle,
} from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import type { UploadStartPhase } from '@/api/pricing/stageWorkbooks';
import { FileDropzone } from '../../../item-master/components/FileDropzone';
import { ToolCard } from '../ToolCard';
import { HUBBELL_FEEDBACK_ACCEPT } from '../hubbell-feedback/hubbellFeedbackFile';
import {
  downloadsAvailable,
  isRunActive,
} from '../hubbell-feedback/runDisplay';
import {
  RENEWAL_SLOTS,
  type DroppedRenewalFile,
  type RenewalDropState,
  type RenewalSlot,
} from './classifyRenewalFiles';
import {
  divisionDownloads,
  divisionOutcomes,
  isDownloading,
  skippedLineCount,
  type RenewalDownloadTarget,
  type RenewalStartBlocker,
} from './massRenewalRunDisplay';
import {
  divisionSkipReasonText,
  downloadFileText,
  fileKindText,
  looksLikeText,
  renewalSlotText,
  renewalStartBlockerText,
  skippedBreakdownText,
  unmatchedExceptionReasonText,
  validateRenewalFile,
} from './massRenewalText';

type MoveFile = (id: string, slot: RenewalSlot) => void;

export interface MassRenewalCardViewProps {
  supplier: FeedbackSupplierProfile;
  run: MassRenewalRun | null;
  runLoading: boolean;
  runError: boolean;
  drop: RenewalDropState;
  phase: UploadStartPhase | null;
  blocker: RenewalStartBlocker | null;
  downloading: RenewalDownloadTarget | null;
  /** How many Renewal Rules the supplier has, for the rules section's heading. */
  ruleCount: number;
  /** The supplier's Renewal Rules panel, mounted in a collapsible section. */
  rulesPanel: ReactNode;
  /** Files added under a category (its upload area). */
  onFiles: (files: File[], slot: RenewalSlot) => void;
  /** A file put under another category from its menu. */
  onMove: MoveFile;
  onRemove: (id: string) => void;
  onStart: () => void;
  onDownload: (letter: string, file: MassRenewalDownloadFile) => void;
  onDownloadTemplate: () => void;
}

function FileKindBadge({ entry }: { entry: DroppedRenewalFile }) {
  const gt = useGT();
  const text = fileKindText(entry.check, gt);
  if (text === null) return <Spinner size="sm" />;
  return (
    <Badge
      variant={entry.check.kind === entry.slot ? 'secondary' : 'warning'}
      className="max-w-72 truncate"
      title={text}
    >
      {text}
    </Badge>
  );
}

function SlotMenu({
  entry,
  disabled,
  onMove,
}: {
  entry: DroppedRenewalFile;
  disabled: boolean;
  onMove: MoveFile;
}) {
  const gt = useGT();
  return (
    <Select
      value={entry.slot}
      onValueChange={(next) => {
        const slot = RENEWAL_SLOTS.find((candidate) => candidate === next);
        if (slot && slot !== entry.slot) onMove(entry.id, slot);
      }}
    >
      <SelectTrigger
        size="sm"
        className="h-6 w-40 shrink-0 text-xs"
        disabled={disabled}
        aria-label={gt('Category of {name}', {
          context:
            'Mass Renewal card: menu that changes which input a dropped file is',
          name: entry.file.name,
        })}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {RENEWAL_SLOTS.map((slot) => (
          <SelectItem key={slot} value={slot}>
            {renewalSlotText(slot, gt)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** Where a misplaced file seems to belong, under its row. */
function LooksLikeHint({ entry }: { entry: DroppedRenewalFile }) {
  const gt = useGT();
  const { check } = entry;
  if (!('looksLike' in check) || !check.looksLike) return null;
  return (
    <p className="text-xs text-amber-700 dark:text-amber-500">
      {looksLikeText(check.looksLike, gt)}
    </p>
  );
}

function DroppedFile({
  entry,
  disabled,
  onMove,
  onRemove,
}: {
  entry: DroppedRenewalFile;
  disabled: boolean;
  onMove: MoveFile;
  onRemove: (id: string) => void;
}) {
  const gt = useGT();
  return (
    <li className="flex flex-col gap-0.5">
      <div className="flex items-center gap-2 text-xs">
        <span className="min-w-0 flex-1 truncate" title={entry.file.name}>
          {entry.file.name}
        </span>
        <FileKindBadge entry={entry} />
        <SlotMenu entry={entry} disabled={disabled} onMove={onMove} />
        <Button
          variant="ghost"
          size="icon"
          className="size-6"
          disabled={disabled}
          aria-label={gt('Remove {name}', {
            context: 'Mass Renewal card: remove one dropped file',
            name: entry.file.name,
          })}
          onClick={() => onRemove(entry.id)}
        >
          <XIcon className="h-4 w-4" />
        </Button>
      </div>
      <LooksLikeHint entry={entry} />
    </li>
  );
}

function SlotHeading({
  slot,
  supplier,
}: {
  slot: RenewalSlot;
  supplier: FeedbackSupplierProfile;
}) {
  switch (slot) {
    case 'results':
      return (
        <T context="Mass Renewal card: heading of the Results Workbook upload area; the variable names the supplier's Results Workbook">
          Results Workbook (<Var>{supplier.resultsFileLabel}</Var>)
        </T>
      );
    case 'feedback':
      return (
        <T context="Mass Renewal card: heading of the Feedback Files upload area">
          Feedback Files (one per Division, up to nine)
        </T>
      );
    case 'exceptions':
      return (
        <T context="Mass Renewal card: heading of the Exception Lists upload area; product hold lists go here too">
          Exception Lists and Product Holds (optional, up to 20)
        </T>
      );
  }
}

function SlotPrompt({ slot }: { slot: RenewalSlot }) {
  switch (slot) {
    case 'results':
      return (
        <T context="instruction inside the Results Workbook upload area of the Mass Renewal card">
          Drop the Results Workbook here.
        </T>
      );
    case 'feedback':
      return (
        <T context="instruction inside the Feedback Files upload area of the Mass Renewal card">
          Drop the filled-in Feedback Files here.
        </T>
      );
    case 'exceptions':
      return (
        <T context="instruction inside the Exception Lists upload area of the Mass Renewal card">
          Drop Exception Lists and Product Hold lists here.
        </T>
      );
  }
}

/** One input's upload area and the files under it. */
function SlotSection({
  slot,
  supplier,
  files,
  disabled,
  onFiles,
  onMove,
  onRemove,
}: {
  slot: RenewalSlot;
  supplier: FeedbackSupplierProfile;
  files: DroppedRenewalFile[];
  disabled: boolean;
  onFiles: (files: File[], slot: RenewalSlot) => void;
  onMove: MoveFile;
  onRemove: (id: string) => void;
}) {
  const gt = useGT();
  const inSlot = files.filter((entry) => entry.slot === slot);
  return (
    <section
      className="flex flex-col gap-1 rounded-md border p-2"
      aria-label={renewalSlotText(slot, gt)}
    >
      <h3 className="text-xs font-medium">
        <SlotHeading slot={slot} supplier={supplier} />
      </h3>
      <FileDropzone
        inline
        className="w-full max-w-sm"
        accept={HUBBELL_FEEDBACK_ACCEPT}
        disabled={disabled}
        prompt={<SlotPrompt slot={slot} />}
        validate={(file) =>
          Promise.resolve(validateRenewalFile(file, gt, supplier))
        }
        multiple
        onFilesAccepted={(accepted) => onFiles(accepted, slot)}
      />
      {inSlot.length > 0 ? (
        <ul className="flex flex-col gap-1">
          {inSlot.map((entry) => (
            <DroppedFile
              key={entry.id}
              entry={entry}
              disabled={disabled}
              onMove={onMove}
              onRemove={onRemove}
            />
          ))}
        </ul>
      ) : null}
    </section>
  );
}

function StartPhaseLine({ phase }: { phase: UploadStartPhase }) {
  if (phase.phase === 'starting') {
    return (
      <p className="text-muted-foreground text-xs">
        <T context="Mass Renewal card: the files are uploaded and the run is being started">
          Starting the run…
        </T>
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-1">
      <Progress value={phase.percent} />
      <p className="text-muted-foreground truncate text-xs">
        <T context="Mass Renewal card: upload progress line">
          Uploading <Var>{phase.fileName}</Var>… <Num>{phase.percent}</Num>%
        </T>
      </p>
    </div>
  );
}

function StartControls({
  blocker,
  phase,
  onStart,
}: {
  blocker: RenewalStartBlocker | null;
  phase: UploadStartPhase | null;
  onStart: () => void;
}) {
  const gt = useGT();
  const blockerText = blocker ? renewalStartBlockerText(blocker, gt) : null;
  return (
    <>
      {phase ? <StartPhaseLine phase={phase} /> : null}
      <div className="flex flex-col gap-1">
        <Button
          size="sm"
          disabled={blocker !== null}
          onClick={onStart}
          className="self-start"
        >
          <PlayIcon className="h-4 w-4" />
          <T context="Mass Renewal card: button that uploads the files and starts the run">
            Start Run
          </T>
        </Button>
        {blockerText ? (
          <p className="text-muted-foreground text-xs">{blockerText}</p>
        ) : null}
      </div>
    </>
  );
}

function RunStatusBadge({ status }: { status: BackgroundJobStatus }) {
  switch (status) {
    case BackgroundJobStatus.Queued:
      return (
        <Badge variant="outline">
          <T context="Mass Renewal card: run status">Queued</T>
        </Badge>
      );
    case BackgroundJobStatus.Running:
      return (
        <Badge variant="outline">
          <T context="Mass Renewal card: run status">Running</T>
        </Badge>
      );
    case BackgroundJobStatus.Succeeded:
      return (
        <Badge variant="success">
          <T context="Mass Renewal card: run status">Done</T>
        </Badge>
      );
    case BackgroundJobStatus.Failed:
      return (
        <Badge variant="destructive">
          <T context="Mass Renewal card: run status">Failed</T>
        </Badge>
      );
  }
}

function DownloadButtons({
  division,
  downloading,
  onDownload,
}: {
  division: MassRenewalBuiltDivision;
  downloading: RenewalDownloadTarget | null;
  onDownload: (letter: string, file: MassRenewalDownloadFile) => void;
}) {
  const gt = useGT();
  return (
    <div className="flex flex-wrap gap-1">
      {divisionDownloads(division).map((file) => {
        const busy = isDownloading(downloading, division.letter, file);
        return (
          <Button
            key={file}
            variant="outline"
            size="sm"
            className="h-6 px-2 text-xs"
            disabled={busy}
            onClick={() => onDownload(division.letter, file)}
          >
            {busy ? (
              <Spinner size="sm" />
            ) : (
              <DownloadSimpleIcon className="h-3 w-3" />
            )}
            {downloadFileText(file, gt)}
          </Button>
        );
      })}
    </div>
  );
}

function DivisionHeader({ code, name }: { code: string; name: string }) {
  return (
    <span className="flex items-center gap-1">
      <span className="bg-muted shrink-0 rounded px-1 font-mono">{code}</span>
      <span className="min-w-0 flex-1 truncate font-medium">{name}</span>
    </span>
  );
}

/** Floored, no-Feedback-row and held lines: only the ones that happened. */
function DivisionDetails({ division }: { division: MassRenewalBuiltDivision }) {
  const { floored, noFeedbackRow } = division.counts;
  return (
    <ul className="text-muted-foreground flex flex-col">
      {division.held.map((held) => (
        <li key={held.ruleId}>
          <T context="Mass Renewal card: lines one Renewal Rule held at no increase">
            Held by <Var>{held.name}</Var>: <Num>{held.count}</Num>
          </T>
        </li>
      ))}
      {floored > 0 ? (
        <li>
          <T context="Mass Renewal card: lines a margin floor raised">
            Raised to the margin floor: <Num>{floored}</Num>
          </T>
        </li>
      ) : null}
      {noFeedbackRow > 0 ? (
        <li>
          <T context="Mass Renewal card: uploaded lines whose Load has no Feedback row, so no increase">
            No Feedback row (unchanged): <Num>{noFeedbackRow}</Num>
          </T>
        </li>
      ) : null}
      {division.errors.map((error) => (
        <li key={error.sheetRow} className="text-destructive">
          <T context="Mass Renewal card: one line error of a Division, by its sheet row">
            Row <Num>{error.sheetRow}</Num>: <Var>{error.message}</Var>
          </T>
        </li>
      ))}
    </ul>
  );
}

/** A cost-unchanged hold that could not compare some lines' cost. */
function CostHoldWarning({
  warning,
  results,
}: {
  warning: MassRenewalDivisionWarning;
  /** The supplier's Results Workbook as the team names it ("FINAL.xlsx"). */
  results: string;
}) {
  const { lines } = warning;
  switch (warning.warning) {
    case 'no PRIOR CORE COST NET column':
      return (
        <T context="Mass Renewal card: a Division's Hold when cost unchanged rule could not compare costs because the Results Workbook has no PRIOR CORE COST NET column; such lines are not held">
          Hold when cost unchanged could not check{' '}
          <Plural
            n={lines}
            one={<>1 line</>}
            other={
              <>
                <Num>{lines}</Num> lines
              </>
            }
          />
          : <Var>{results}</Var> has no PRIOR CORE COST NET column
        </T>
      );
    case 'blank PRIOR CORE COST NET':
      return (
        <T context="Mass Renewal card: a Division's Hold when cost unchanged rule could not compare costs on lines whose PRIOR CORE COST NET is blank; such lines are not held">
          Hold when cost unchanged could not check{' '}
          <Plural
            n={lines}
            one={<>1 line</>}
            other={
              <>
                <Num>{lines}</Num> lines
              </>
            }
          />
          : PRIOR CORE COST NET is blank
        </T>
      );
  }
}

/** Failed lines are in neither upload: said beside the downloads, so no one uploads without them unawares. */
function FailedLinesWarning({ errors }: { errors: number }) {
  if (errors === 0) return null;
  return (
    <p className="text-destructive flex items-start gap-1 font-medium">
      <WarningIcon className="mt-0.5 h-3 w-3 shrink-0" />
      <span>
        <T context="Mass Renewal card: a built Division's lines that failed are left out of its upload CSV; the Renewal Report's Failed Lines sheet lists them">
          <Plural
            n={errors}
            one={
              <>
                1 line failed and is not in the upload — see Failed Lines in the
                report
              </>
            }
            other={
              <>
                <Num>{errors}</Num> lines failed and are not in the upload — see
                Failed Lines in the report
              </>
            }
          />
        </T>
      </span>
    </p>
  );
}

function BuiltDivision({
  division,
  results,
  downloadable,
  downloading,
  onDownload,
}: {
  division: MassRenewalBuiltDivision;
  results: string;
  downloadable: boolean;
  downloading: RenewalDownloadTarget | null;
  onDownload: (letter: string, file: MassRenewalDownloadFile) => void;
}) {
  const gt = useGT();
  const { uploaded, exceptions, errors } = division.counts;
  return (
    <li className="flex min-w-0 flex-col gap-1 rounded-md border p-2 text-xs">
      <DivisionHeader code={division.code} name={division.name} />
      <span title={skippedBreakdownText(division.counts.skipped, gt)}>
        <T context="Mass Renewal card: a built Division's line counts">
          <Num>{uploaded}</Num> uploaded · <Num>{exceptions}</Num> exceptions ·{' '}
          <Num>{skippedLineCount(division.counts)}</Num> skipped ·{' '}
          <Num>{errors}</Num> errors
        </T>
      </span>
      <DivisionDetails division={division} />
      {division.warnings.map((warning) => (
        <p
          key={warning.warning}
          className="flex items-start gap-1 text-amber-700 dark:text-amber-500"
        >
          <WarningIcon className="mt-0.5 h-3 w-3 shrink-0" />
          <span>
            <CostHoldWarning warning={warning} results={results} />
          </span>
        </p>
      ))}
      <FailedLinesWarning errors={errors} />
      {downloadable ? (
        <DownloadButtons
          division={division}
          downloading={downloading}
          onDownload={onDownload}
        />
      ) : null}
    </li>
  );
}

function SkippedDivision({
  division,
  results,
}: {
  division: MassRenewalSkippedDivision;
  results: string;
}) {
  const gt = useGT();
  return (
    <li className="text-muted-foreground text-xs">
      <span className="bg-muted mr-1 rounded px-1 font-mono">
        {division.code ?? division.letter}
      </span>
      {divisionSkipReasonText(division.reason, gt, results, division.share)}
    </li>
  );
}

function DivisionResults({
  run,
  results,
  downloading,
  onDownload,
}: {
  run: MassRenewalRun;
  results: string;
  downloading: RenewalDownloadTarget | null;
  onDownload: (letter: string, file: MassRenewalDownloadFile) => void;
}) {
  const outcomes = divisionOutcomes(run);
  const downloadable = downloadsAvailable(run);
  const built = outcomes.flatMap((outcome) =>
    outcome.status === 'built' ? [outcome] : [],
  );
  const skipped = outcomes.flatMap((outcome) =>
    outcome.status === 'skipped' ? [outcome] : [],
  );
  return (
    <>
      {built.length > 0 ? (
        <ul className="grid gap-2 sm:grid-cols-2">
          {built.map((division) => (
            <BuiltDivision
              key={division.letter}
              division={division}
              results={results}
              downloadable={downloadable}
              downloading={downloading}
              onDownload={onDownload}
            />
          ))}
        </ul>
      ) : null}
      {skipped.length > 0 ? (
        <ul className="flex flex-col gap-0.5">
          {skipped.map((division) => (
            <SkippedDivision
              key={division.letter}
              division={division}
              results={results}
            />
          ))}
        </ul>
      ) : null}
    </>
  );
}

/** Exception List rows that priced no line, and Feedback Files that named no Division. */
function ReportLeftovers({ report }: { report: MassRenewalRunReport }) {
  const gt = useGT();
  const { unmatchedExceptionRows, unknownFeedbackFiles } = report;
  return (
    <>
      {unmatchedExceptionRows.length > 0 ? (
        <div className="text-xs">
          <p className="font-medium">
            <T context="Mass Renewal card: heading of the Exception List rows that priced no line">
              Exception List rows that priced no line
            </T>
          </p>
          <ul className="text-muted-foreground max-h-32 overflow-y-auto font-mono">
            {unmatchedExceptionRows.map((row) => (
              <li key={`${row.sourceFile ?? ''}:${row.sheetRow}`}>
                <T context="Mass Renewal card: one Exception List row that priced no line: its sheet row, AS400 item, SELL NET and why">
                  <Var>{row.sourceFile ?? ''}</Var> Row{' '}
                  <Num>{row.sheetRow}</Num>: <Var>{row.item}</Var> at{' '}
                  <Var>
                    {row.margin !== undefined ? `${row.margin}%` : row.sellNet}
                  </Var>{' '}
                  — <Var>{unmatchedExceptionReasonText(row, gt)}</Var>
                </T>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {unknownFeedbackFiles.length > 0 ? (
        <p className="text-muted-foreground text-xs">
          <T context="Mass Renewal card: Feedback Files of the run with no Feedback sheet of a known Division">
            Not used (no Division Feedback sheet):{' '}
            <Var>{unknownFeedbackFiles.join('; ')}</Var>
          </T>
        </p>
      ) : null}
    </>
  );
}

/** What stands between the viewer and the files: expired, someone else's, or a failed run. */
function RunNotice({ run }: { run: MassRenewalRun }) {
  const gt = useGT();
  const succeeded = run.status === BackgroundJobStatus.Succeeded;
  const starter =
    run.startedByName ??
    gt('another user', {
      context: 'Mass Renewal card: the starter of a run whose name is unknown',
    });
  return (
    <>
      {succeeded && run.startedByCurrentUser && !downloadsAvailable(run) ? (
        <p className="text-muted-foreground text-xs">
          <T context="Mass Renewal card: the latest run's files have passed the 30-day retention">
            The files of this run have expired; start a run to get fresh ones.
          </T>
        </p>
      ) : null}
      {succeeded && !run.startedByCurrentUser ? (
        <p className="text-muted-foreground text-xs">
          <T context="Mass Renewal card: the latest run was started by someone else, whose downloads these are">
            Started by <Var>{starter}</Var>; the downloads are theirs. Start a
            run to get your own.
          </T>
        </p>
      ) : null}
      {run.status === BackgroundJobStatus.Failed ? (
        <AlertContainer variant="destructive">
          <AlertTitle>
            <T context="Mass Renewal card: the run failed">The run failed</T>
          </AlertTitle>
          <AlertDescription>
            {run.errorMessage ??
              gt('Something went wrong; try the drop again.', {
                context:
                  'Mass Renewal card: a failed run with no message from the server',
              })}
          </AlertDescription>
        </AlertContainer>
      ) : null}
    </>
  );
}

function RunSummary({ run }: { run: MassRenewalRun }) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="font-medium">
          <T context="Mass Renewal card: heading of the latest run section">
            Latest run
          </T>
        </span>
        <RunStatusBadge status={run.status} />
        <span className="text-muted-foreground">
          <DateTime>{run.createdAt}</DateTime>
        </span>
        <span className="text-muted-foreground">
          <T context="Mass Renewal card: how many enabled Renewal Rules the run was started with">
            Renewal Rules used: <Num>{run.ruleCount}</Num>
          </T>
        </span>
      </div>
      <p className="text-muted-foreground truncate text-xs">
        <T context="Mass Renewal card: the latest run's inputs: Results Workbook name and number of Feedback Files">
          <Var>{run.inputs.resultsWorkbook.fileName}</Var> ·{' '}
          <Num>{run.inputs.feedbackFiles.length}</Num> Feedback Files
        </T>
        {run.inputs.exceptionLists?.length || run.inputs.exceptionList ? (
          <T context="Mass Renewal card: the Exception Lists the latest run was given">
            {' '}
            · Exceptions:{' '}
            <Var>
              {[
                ...(run.inputs.exceptionLists ?? []),
                ...(run.inputs.exceptionList ? [run.inputs.exceptionList] : []),
              ]
                .map((file) => file.fileName)
                .join('; ')}
            </Var>
          </T>
        ) : null}
      </p>
      {isRunActive(run) ? (
        <Progress
          value={run.percent ?? 100}
          className={run.percent === null ? 'animate-pulse' : undefined}
        />
      ) : null}
    </div>
  );
}

function LatestRun({
  run,
  loading,
  error,
  results,
  downloading,
  onDownload,
}: {
  run: MassRenewalRun | null;
  loading: boolean;
  error: boolean;
  results: string;
  downloading: RenewalDownloadTarget | null;
  onDownload: (letter: string, file: MassRenewalDownloadFile) => void;
}) {
  if (loading) return <Spinner size="sm" />;
  if (error)
    return (
      <p className="text-destructive text-xs">
        <T context="Mass Renewal card: the latest run could not be loaded">
          The latest run could not be loaded.
        </T>
      </p>
    );
  if (!run) return null;
  return (
    <div className="flex flex-col gap-2">
      <RunSummary run={run} />
      <RunNotice run={run} />
      <DivisionResults
        run={run}
        results={results}
        downloading={downloading}
        onDownload={onDownload}
      />
      {run.report ? <ReportLeftovers report={run.report} /> : null}
    </div>
  );
}

function RulesSection({
  ruleCount,
  children,
}: {
  ruleCount: number;
  children: ReactNode;
}) {
  return (
    <Collapsible className="border-t pt-2">
      <CollapsibleTrigger className="group flex w-full items-center gap-2 text-left text-sm font-medium">
        <CaretRightIcon className="h-3 w-3 transition-transform group-data-[panel-open]:rotate-90" />
        <T context="Mass Renewal card: collapsible section holding the supplier's Renewal Rules, with their count">
          Renewal Rules (<Num>{ruleCount}</Num>)
        </T>
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-2">{children}</CollapsibleContent>
    </Collapsible>
  );
}

/**
 * A supplier's Mass Renewal card: add the Results Workbook, the filled-in
 * Feedback Files and optional Exception Lists, each to its own section (a
 * file's menu moves it to another), start the run, watch it,
 * and download each Division's upload, exceptions and report. The org's
 * latest run is shown whether or not this page started it.
 */
export function MassRenewalCardView({
  supplier,
  run,
  runLoading,
  runError,
  drop,
  phase,
  blocker,
  downloading,
  ruleCount,
  rulesPanel,
  onFiles,
  onMove,
  onRemove,
  onStart,
  onDownload,
  onDownloadTemplate,
}: MassRenewalCardViewProps) {
  const starting = phase !== null;
  return (
    <ToolCard
      fixedHeight={false}
      title={
        <T context="heading of a supplier's Mass Renewal card, e.g. Hubbell Mass Renewal">
          <Var>{supplier.name}</Var> Mass Renewal
        </T>
      }
      description={
        <T context="explanation under a supplier's Mass Renewal card heading; the variable names the supplier's Results Workbook">
          Add <Var>{supplier.resultsFileLabel}</Var>, the filled-in Feedback
          Files and optional Exception Lists (up to 20) to their sections;
          change a file's category if it is in the wrong one. One upload CSV per
          Division. Product-only lists hold margin and core cost increases for
          their Division in this run. For price exceptions, enter a SELL NET or
          a target Margin (22 means 22%), an optional Core Cost adder (5 means
          5%), and Add CC to Sell Y or N (blank means N).
        </T>
      }
      action={
        <Button size="sm" variant="outline" onClick={onDownloadTemplate}>
          <FileXlsIcon className="h-4 w-4" />
          <T context="Mass Renewal card: button that downloads an empty Exception List workbook with its headers">
            Download Exception List Template
          </T>
        </Button>
      }
    >
      <div className="flex flex-col gap-2">
        {RENEWAL_SLOTS.map((slot) => (
          <SlotSection
            key={slot}
            slot={slot}
            supplier={supplier}
            files={drop.files}
            disabled={starting}
            onFiles={onFiles}
            onMove={onMove}
            onRemove={onRemove}
          />
        ))}
        <StartControls blocker={blocker} phase={phase} onStart={onStart} />
        <LatestRun
          run={run}
          loading={runLoading}
          error={runError}
          results={supplier.resultsFileLabel}
          downloading={downloading}
          onDownload={onDownload}
        />
        <RulesSection ruleCount={ruleCount}>{rulesPanel}</RulesSection>
      </div>
    </ToolCard>
  );
}
